"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import type { CategorySelection } from "@/lib/types";
import {
  BULK_LISTING_MAX_ITEMS,
  BULK_LISTING_MIN_ITEMS,
  createEmptyBulkItemDraft,
  defaultBulkListingCommon,
  type BulkListingCommonState,
  type BulkListingItemDraft,
  type SellListingMode,
} from "@/lib/listing/bulkListingTypes";

function itemHasMeaningfulContent(d: BulkListingItemDraft): boolean {
  return (
    d.title.trim().length > 0 ||
    d.price.trim().length > 0 ||
    Boolean(d.category) ||
    d.images.length > 0 ||
    d.description.trim().length > 0 ||
    d.defects.trim().length > 0 ||
    d.brand.trim().length > 0 ||
    d.size.trim().length > 0 ||
    d.model.trim().length > 0
  );
}

export function useBulkListing() {
  const [listingMode, setListingMode] = useState<SellListingMode>("single");
  const [bulkCommon, setBulkCommon] = useState<BulkListingCommonState>(() =>
    defaultBulkListingCommon(),
  );
  const [bulkItems, setBulkItems] = useState<BulkListingItemDraft[]>(() => [
    createEmptyBulkItemDraft(),
  ]);
  const [bulkItemIndex, setBulkItemIndex] = useState(0);
  /** One DB batch id per submit session; retries reuse; cleared on full success. */
  const bulkSubmitBatchIdRef = useRef<string | null>(null);

  const currentBulkItem =
    bulkItems[bulkItemIndex] ?? bulkItems[0] ?? createEmptyBulkItemDraft();

  const isBulkDirty = useMemo(() => {
    if (listingMode !== "bulk") return false;
    if (bulkCommon.location.trim()) return true;
    if (bulkCommon.pickupDays.length > 0) return true;
    if (bulkItems.length > 1) return true;
    return bulkItems.some(itemHasMeaningfulContent);
  }, [listingMode, bulkCommon, bulkItems]);

  const resolveBulkSubmitBatchId = useCallback((retryFailedOnly: boolean) => {
    if (!retryFailedOnly || !bulkSubmitBatchIdRef.current) {
      bulkSubmitBatchIdRef.current = uuidv4();
    }
    return bulkSubmitBatchIdRef.current;
  }, []);

  const rememberBulkSubmitBatchId = useCallback((id: string) => {
    bulkSubmitBatchIdRef.current = id;
  }, []);

  const clearBulkSubmitBatchId = useCallback(() => {
    bulkSubmitBatchIdRef.current = null;
  }, []);

  const resetBulkState = useCallback(() => {
    setBulkCommon(defaultBulkListingCommon());
    setBulkItems([createEmptyBulkItemDraft()]);
    setBulkItemIndex(0);
    bulkSubmitBatchIdRef.current = null;
  }, []);

  const updateBulkItem = useCallback(
    (id: string, patch: Partial<BulkListingItemDraft>) => {
      setBulkItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? {
                ...it,
                ...patch,
                submitError: undefined,
                lastSubmitSucceeded: false,
              }
            : it,
        ),
      );
    },
    [],
  );

  const onBulkCategorySelect = useCallback(
    (id: string, selection: CategorySelection) => {
      updateBulkItem(id, {
        category: selection.categoryId,
        subcategory: selection.subcategoryId,
        brand: "",
        size: "",
        model: "",
        rulesAcknowledged: false,
      });
    },
    [updateBulkItem],
  );

  const addBulkItem = useCallback(() => {
    setBulkItems((prev) => {
      if (prev.length >= BULK_LISTING_MAX_ITEMS) return prev;
      const next = [...prev, createEmptyBulkItemDraft()];
      setBulkItemIndex(next.length - 1);
      return next;
    });
  }, []);

  const removeBulkItem = useCallback((id: string) => {
    setBulkItems((prev) => {
      if (prev.length <= BULK_LISTING_MIN_ITEMS) return prev;
      const idx = prev.findIndex((x) => x.id === id);
      if (idx < 0) return prev;
      const next = prev.filter((x) => x.id !== id);
      setBulkItemIndex((i) => {
        if (i > idx) return Math.max(0, i - 1);
        if (i === idx) return Math.min(i, next.length - 1);
        return i;
      });
      return next;
    });
  }, []);

  const duplicateBulkItem = useCallback((id: string) => {
    setBulkItems((prev) => {
      if (prev.length >= BULK_LISTING_MAX_ITEMS) return prev;
      const source = prev.find((x) => x.id === id);
      if (!source) return prev;
      const copy: BulkListingItemDraft = {
        ...createEmptyBulkItemDraft(),
        title: source.title,
        description: source.description,
        defects: source.defects,
        price: source.price,
        category: source.category,
        subcategory: source.subcategory,
        quantity: source.quantity,
        brand: source.brand,
        size: source.size,
        model: source.model,
        rulesAcknowledged: source.rulesAcknowledged,
      };
      const idx = prev.findIndex((x) => x.id === id);
      const next = [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
      setBulkItemIndex(idx + 1);
      return next;
    });
  }, []);

  const setBulkSubmitResult = useCallback(
    (id: string, result: { error?: string; succeeded?: boolean }) => {
      setBulkItems((prev) =>
        prev.map((it) => {
          if (it.id !== id) return it;
          if (result.succeeded) {
            return {
              ...it,
              submitError: undefined,
              lastSubmitSucceeded: true,
            };
          }
          if (result.error !== undefined) {
            return {
              ...it,
              submitError: result.error,
              lastSubmitSucceeded: false,
            };
          }
          return it;
        }),
      );
    },
    [],
  );

  return {
    listingMode,
    setListingMode,
    bulkCommon,
    setBulkCommon,
    bulkItems,
    setBulkItems,
    bulkItemIndex,
    setBulkItemIndex,
    currentBulkItem,
    isBulkDirty,
    resetBulkState,
    updateBulkItem,
    onBulkCategorySelect,
    addBulkItem,
    removeBulkItem,
    duplicateBulkItem,
    setBulkSubmitResult,
    resolveBulkSubmitBatchId,
    rememberBulkSubmitBatchId,
    clearBulkSubmitBatchId,
  };
}
